import { z } from 'zod';

// Display snapshot of the connected account, resolved entirely server-side
// (personal token at OAuth, or org-mapped member at map time). `null` when
// there's no connection; individual fields can be `null` for connections made
// before the snapshot shipped (no backfill) — fall back `name` → `login` →
// generic copy.
export const BizyConnectorDreamingIdentitySchema = z.object({
	login: z.string().nullable(),
	name: z.string().nullable(),
	avatarUrl: z.string().nullable(),
});
export type BizyConnectorDreamingIdentity = z.infer<typeof BizyConnectorDreamingIdentitySchema>;

// Per-connector dreaming status for the current user (BIZY-540 backend
// contract). `enabled` is the user's opt-out state (default-on). `applicable`
// means Bizy actually dreams about this user for the connector (personally
// connected OR org-configured + mapped). `personallyConnected` drives the
// "connect your own GitHub" upsell, and `companyConfigured` means an org
// connector can back dreaming. `identity` describes the connected account so
// the member can validate the right GitHub user is attached.
export const BizyConnectorDreamingPreferenceSchema = z.object({
	enabled: z.boolean(),
	applicable: z.boolean(),
	personallyConnected: z.boolean(),
	companyConfigured: z.boolean(),
	identity: BizyConnectorDreamingIdentitySchema.nullable().optional(),
});
export type BizyConnectorDreamingPreference = z.infer<typeof BizyConnectorDreamingPreferenceSchema>;

// Keyed by connector slug (e.g. `github`). Modeled as a record so new
// connectors don't require an FE change to be read.
export const BizyDreamingPreferencesSchema = z.object({
	dreamingPreferences: z.record(z.string(), BizyConnectorDreamingPreferenceSchema),
});
export type BizyDreamingPreferences = z.infer<typeof BizyDreamingPreferencesSchema>;

export const bizyConnectorDreamingPreferenceFixture = (overrides: Partial<BizyConnectorDreamingPreference> = {}): BizyConnectorDreamingPreference => ({
	enabled: true,
	applicable: true,
	personallyConnected: false,
	companyConfigured: true,
	identity: { login: 'octocat', name: 'The Octocat', avatarUrl: 'https://avatars.githubusercontent.com/u/583231' },
	...overrides,
});

export const bizyDreamingPreferencesFixture = (
	connectors: Record<string, Partial<BizyConnectorDreamingPreference>> = { github: {} }
): BizyDreamingPreferences => ({
	dreamingPreferences: Object.fromEntries(
		Object.entries(connectors).map(([connector, overrides]) => [connector, bizyConnectorDreamingPreferenceFixture(overrides)])
	),
});
